import React, { useState } from "react";
import { FaSearch } from "react-icons/fa";

// --- IMPORTS COMPONENTES ---
import ClienteAutocomplete from "../../../components/autocomplete/ClienteAutocomplete";
import EessAutocomplete from "../../../components/autocomplete/EessAutocomplete";
import LiquidacionEjecucionAutocomplete from "../../../components/autocomplete/LiquidacionEjecucionAutocomplete";
import ProductoAutocomplete from "../../../components/autocomplete/ProductoAutocomplete";

// --- IMPORTS API / AUTH ---
import apiRoutes from "../../../api/apiRoutes";
import { appFetchBlob } from "../../../utils/http";

// --- CONSTANTES ---
const FORMATO_OPCIONES = [
  { id: "EXCEL", label: "EXCEL" },
  { id: "PDF", label: "PDF" },
];

const TIPO_TARJETA_OPCIONES = [
  { id: "CLIENTE", label: "Cliente" },
  { id: "MAESTRA", label: "Maestra" },
  { id: "OTROS", label: "Otros" },
];

export default function ListarTransaccionesManuales() {
  const [descargando, setDescargando] = useState(false);
  const [errorValidacion, setErrorValidacion] = useState(""); // Estado para el banner

  // --- ESTADOS DEL FORMULARIO ---
  const [cliente, setCliente] = useState(null);
  const [eess, setEess] = useState(null);
  // --- FECHAS POR DEFECTO (MES ANTERIOR) ---
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  
  // restar 1 mes manteniendo el mismo día
  const haceUnMes = new Date(hoy);
  haceUnMes.setMonth(haceUnMes.getMonth() - 1);
  
  // ajuste por meses con menos días (ej: 31 → 28/29)
  if (haceUnMes.getDate() !== hoy.getDate()) {
    haceUnMes.setDate(0); // último día del mes anterior
  }
  
  const formatDate = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };
  
  const [fechaDesde, setFechaDesde] = useState(formatDate(haceUnMes));
  const [fechaHasta, setFechaHasta] = useState(formatDate(hoy));
  const [nroTarjeta, setNroTarjeta] = useState("");
  const [liqDesde, setLiqDesde] = useState(null);
  const [liqHasta, setLiqHasta] = useState(null);
  const [producto, setProducto] = useState(null);
  const [centroCosto, setCentroCosto] = useState("");
  const [tipoTarjeta, setTipoTarjeta] = useState("");
  const [formato, setFormato] = useState("EXCEL");

  // --- HANDLER CONSULTAR ---
  const handleConsultar = async () => {
    setErrorValidacion(""); // Resetear error al iniciar

    const tieneDesde = !!fechaDesde;
    const tieneHasta = !!fechaHasta;

    // 1. VALIDACIÓN: O ambos vacíos (rango automático) o ambos llenos
    if ((tieneDesde && !tieneHasta) || (!tieneDesde && tieneHasta)) {
      setErrorValidacion(
        "Para filtrar por fecha debe completar tanto 'Fecha Desde' como 'Fecha Hasta'. Si desea el rango automático de 30 días, deje ambos campos vacíos."
      );
      return;
    }

    // 2. VALIDACIÓN: Coherencia de fechas (solo si no están vacías)
    if (
      tieneDesde &&
      tieneHasta &&
      new Date(fechaDesde) > new Date(fechaHasta)
    ) {
      setErrorValidacion(
        "La 'Fecha Desde' no puede ser mayor a la 'Fecha Hasta'."
      );
      return;
    }

    setDescargando(true);
    try {
      const params = new URLSearchParams();
      if (cliente?.id) params.append("clienteId", cliente.id);
      if (eess?.id) params.append("eessId", eess.id);

      // Solo enviamos fechas si el usuario las seleccionó
      if (tieneDesde) params.append("fechaDesde", fechaDesde);
      if (tieneHasta) params.append("fechaHasta", fechaHasta);

      if (nroTarjeta) params.append("tarjetaNumero", nroTarjeta);
      if (liqDesde?.id) params.append("liquidacionDesdeId", liqDesde.id);
      if (liqHasta?.id) params.append("liquidacionHastaId", liqHasta.id);
      if (producto?.id) params.append("productoId", producto.id);
      if (centroCosto) params.append("centroCosto", centroCosto);
      if (tipoTarjeta) params.append("tipo", tipoTarjeta);

      // Usamos las rutas específicas para Transacciones Manuales
      const url =
        formato === "PDF"
          ? apiRoutes.transacciones.exportarTransaccionesManualesPDF(params)
          : apiRoutes.transacciones.exportarTransaccionesManualesExcel(params);

      const { blob, filename } = await appFetchBlob(
        url,
        { method: "GET" },
        { showLoading: false }
      );

      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      const timestamp = new Date().getTime();
      const extension = formato === "PDF" ? "pdf" : "xlsx";

      // Nombre de archivo
      a.download =
        filename || `Reporte_Transacciones_Manuales_${timestamp}.${extension}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error(error);
      if (error.status !== 401) {
        setErrorValidacion("No se pudo descargar el reporte.");
      }
    } finally {
      setDescargando(false);
    }
  };

  return (
    <div className="w-full p-4 md:p-6">
      <div className="bg-white border rounded-md shadow-sm">
        {/* HEADER */}
        <div className="px-4 md:px-6 py-4 border-b flex flex-col md:flex-row md:items-center md:justify-between bg-gray-50 gap-2">
          <h2 className="text-lg md:text-xl font-semibold text-gray-800">
            Detalle de Transacciones Manuales
          </h2>
          <div className="text-sm font-bold text-red-600 bg-red-50 px-3 py-1 rounded-full border border-red-200">
            * Campos obligatorios.
          </div>
        </div>

        {/* BODY */}
        <div className="p-4 md:p-6">
          {/* BANNER DE ERROR */}
          {errorValidacion && (
            <div className="mb-6 bg-[#F8D7DA] border border-[#f5c6cb] text-[#721c24] px-4 py-3 rounded flex items-start gap-3">
              <div className="mt-1">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="12" cy="12" r="10" fill="#dc3545" />
                  <path
                    d="M15 9L9 15M9 9L15 15"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <div className="text-sm">
                <ul className="list-disc list-inside ml-2 mt-1">
                  <li>{errorValidacion}</li>
                </ul>
              </div>
            </div>
          )}

          <div className="bg-gray-100 p-4 md:p-8 rounded-md space-y-4 md:space-y-5 max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2">
  <label className="md:w-60 shrink-0 font-bold text-gray-700">
    Cliente:
  </label>
              <div className="w-full">
                <ClienteAutocomplete
                  value={cliente}
                  onChange={(val) => {
                    setCliente(val);
                    setErrorValidacion("");
                  }}
                />
              </div>
            </div>

            <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2">
  <label className="md:w-60 shrink-0 font-bold text-gray-700">
    EE.SS.:
  </label>
              <div className="w-full">
                <EessAutocomplete
                  value={eess}
                  onChange={(val) => {
                    setEess(val);
                    setErrorValidacion("");
                  }}
                />
              </div>
            </div>

            <div className="flex flex-col space-y-4">
              <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2">
  <label className="md:w-60 shrink-0 font-bold text-gray-700">Fecha Desde*:</label>
                <input
                  type="date"
                  className="input w-full"
                  value={fechaDesde}
                  onChange={(e) => {
                    setFechaDesde(e.target.value);
                    setErrorValidacion("");
                  }}
                />
              </div>
              <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2">
  <label className="md:w-60 shrink-0 font-bold text-gray-700">Fecha Hasta*:</label>
                <input
                  type="date"
                  className="input w-full"
                  value={fechaHasta}
                  onChange={(e) => {
                    setFechaHasta(e.target.value);
                    setErrorValidacion("");
                  }}
                />
              </div>
            </div>

            <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2">
  <label className="md:w-60 shrink-0 font-bold text-gray-700">
    Nro. Tarjeta:
  </label>
              <input
                type="text"
                className="input w-full"
                placeholder="Ingrese número de tarjeta..."
                value={nroTarjeta}
                onChange={(e) => {
                  setNroTarjeta(e.target.value);
                  setErrorValidacion("");
                }}
              />
            </div>

            <div className="flex flex-col space-y-4">
              <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2">
  <label className="md:w-60 shrink-0 font-bold text-gray-700">Liquidación Desde:</label>
                <div className="w-full">
                  <LiquidacionEjecucionAutocomplete
                    value={liqDesde}
                    onChange={(val) => {
                      setLiqDesde(val);
                      setErrorValidacion("");
                    }}
                  />
                </div>
              </div>
              <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2">
  <label className="md:w-60 shrink-0 font-bold text-gray-700">Liquidación Hasta:</label>
                <div className="w-full">
                  <LiquidacionEjecucionAutocomplete
                    value={liqHasta}
                    onChange={(val) => {
                      setLiqHasta(val);
                      setErrorValidacion("");
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col space-y-4">
              <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2">
  <label className="md:w-60 shrink-0 font-bold text-gray-700">Cod. Producto:</label>
                <div className="w-full">
                  <ProductoAutocomplete
                    value={producto}
                    onChange={(val) => {
                      setProducto(val);
                      setErrorValidacion("");
                    }}
                  />
                </div>
              </div>
             <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2">
  <label className="md:w-60 shrink-0 font-bold text-gray-700">Centro de Costo:</label>
                <input
                  type="text"
                  className="input w-full"
                  value={centroCosto}
                  onChange={(e) => {
                    setCentroCosto(e.target.value);
                    setErrorValidacion("");
                  }}
                />
              </div>
            </div>

            <div className="flex flex-col space-y-4">
              <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2">
  <label className="md:w-60 shrink-0 font-bold text-gray-700">Tipo:</label>
                <select
                  className="input w-full"
                  value={tipoTarjeta}
                  onChange={(e) => {
                    setTipoTarjeta(e.target.value);
                    setErrorValidacion("");
                  }}
                >
                  <option value="">-- Todos --</option>
                  {TIPO_TARJETA_OPCIONES.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2">
  <label className="md:w-60 shrink-0 font-bold text-gray-700">Formato:</label>
                <select
                  className="input w-full"
                  value={formato}
                  onChange={(e) => setFormato(e.target.value)}
                >
                  {FORMATO_OPCIONES.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="px-4 md:px-6 py-4 border-t bg-gray-50 flex justify-start">
          <button
            onClick={handleConsultar}
            disabled={descargando}
            className={`flex items-center gap-2 text-white font-semibold py-2 px-10 rounded shadow transition-all ${
              descargando
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-green-800 hover:bg-green-900"
            }`}
          >
            {descargando ? (
              "Generando..."
            ) : (
              <>
                <FaSearch className="text-sm" /> Consultar
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
